// Between the dashboard and its saved copy (dashCache.ts): what a saved
// copy shows on opening (`hydrate`) and what is saved of the dashboard as
// it is shown (`snapshotOf`). Both are pure: reading and writing the copy
// are the store's effects.
import type { DashPr } from '../../api/queries';
import type { AttentionMember } from '../../engine/types';
import type { Section } from '../../logic/sections';
import { prKeyOf } from '../attentionInputs';
import { FORGETS_KEPT_MS, sectionKey, type DashSnapshot } from '../dashCache';
import type { KnownAttention } from './attention';
import type { SectionCut } from './search';
import type { Outside } from './stacks';

/** A section as the saved copy holds it. */
export interface SavedPart {
  prs: DashPr[];
  cut: SectionCut | null;
  /** When its rows were read from GitHub. */
  at: number;
}

/** What a saved copy shows for these sections (see `hydrate`). */
export interface Hydrated {
  /** Each section's saved part, by position; undefined for one the copy lacks. */
  parts: (SavedPart | undefined)[];
  known: KnownAttention;
  /** Rows whose attention the copy shows unknown: worked out again. */
  unknown: DashPr[];
  /** Rows whose saved attention is shown: their reviewers come from the kept inputs. */
  shown: DashPr[];
  /** A section had matches that did not load: it is read again at once. */
  incomplete: boolean;
  outside: Outside;
}

/**
 * Show the sections `saved` holds, as they were read, over what is
 * `known`. A row whose attention the copy does not hold, that `changed`
 * says changed since its section was read (a forget this page knows; the
 * cache already left out the attention of those the log knows), or whose
 * section was read longer ago than forgets are kept (or in the future), is
 * shown without it, and without its reviewers. Null when the copy holds
 * none of these sections.
 */
export function hydrate(
  sections: Section[],
  saved: DashSnapshot,
  known: KnownAttention,
  changed: (key: string, at: number) => boolean,
  now: number,
): Hydrated | null {
  const bySection = new Map(saved.sections.map((s) => [s.key, s]));
  const attention = new Map(known.attention);
  const errors = new Map(known.errors);
  const reviews = new Map(known.reviews);
  const at = new Map(known.at);
  const unknown = new Map<string, DashPr>();
  const shown = new Map<string, DashPr>();
  let incomplete = false;
  const parts = sections.map((section): SavedPart | undefined => {
    const s = bySection.get(sectionKey(section));
    if (!s) return undefined;
    if (s.unloaded) incomplete = true;
    const trusted = s.at <= now && now - s.at < FORGETS_KEPT_MS;
    for (const p of s.prs) {
      const members: AttentionMember[] | undefined = saved.attention[p.id];
      const ticket = saved.attentionTickets[p.id];
      // Its answer is as new as when it was worked out, at most as new as its row.
      if (members && ticket && trusted && !changed(prKeyOf(p.repository.nameWithOwner, p.number), Math.min(ticket.basis, s.at))) {
        attention.set(p.id, members);
        at.set(p.id, ticket);
        reviews.delete(p.id); // filled in from the kept inputs
        shown.set(p.id, p);
      } else {
        // Changed since (or never known): neither its turn nor its reviewers from before are shown.
        attention.delete(p.id);
        at.delete(p.id);
        reviews.delete(p.id);
        unknown.set(p.id, p);
      }
      errors.delete(p.id);
    }
    // Why matches did not load held then, not now: the section is read again (see `incomplete`).
    return { prs: s.prs, cut: s.cut, at: s.at };
  });
  if (!parts.some((p) => p)) return null;
  for (const id of unknown.keys()) shown.delete(id);
  return {
    parts,
    known: { attention, errors, reviews, at },
    unknown: [...unknown.values()],
    shown: [...shown.values()],
    incomplete,
    outside: { prs: saved.outside.prs, hidden: new Set(saved.outside.hidden), sso: new Set() },
  };
}

/** A section as `snapshotOf` saves it: shown, with its rows and when they were read. */
export interface ShownPart {
  section: Section;
  prs: DashPr[];
  at: number;
  cut: SectionCut | null;
  unloaded: number;
}

/**
 * The dashboard as shown, to save for the next open: its shown sections,
 * the attention known for their rows with the ticket each was worked out
 * under, but not that of a pull request `changed` since then (the cache's
 * log leaves out more on writing), and the native stack members read by ID.
 */
export function snapshotOf(
  login: string,
  parts: ShownPart[],
  known: KnownAttention,
  outside: Outside,
  changed: (key: string, at: number) => boolean,
  now: number,
): DashSnapshot {
  const attention: DashSnapshot['attention'] = {};
  const attentionTickets: DashSnapshot['attentionTickets'] = {};
  for (const part of parts)
    for (const p of part.prs) {
      const members = known.attention.get(p.id);
      const ticket = known.at.get(p.id);
      if (!members || !ticket || changed(prKeyOf(p.repository.nameWithOwner, p.number), ticket.basis)) continue;
      attention[p.id] = members;
      attentionTickets[p.id] = ticket;
    }
  return {
    savedAt: now,
    login,
    sections: parts.map((x) => ({ key: sectionKey(x.section), at: x.at, prs: x.prs, cut: x.cut, unloaded: x.unloaded })),
    attention,
    attentionTickets,
    outside: { prs: [...outside.prs], hidden: [...outside.hidden] },
  };
}
